import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  FormControlLabel,
  Switch,
  SelectChangeEvent,
  Stack,
  Tooltip,
  CircularProgress,
  // Checkbox,
  // FormGroup
} from '@mui/material';
import { Add, Edit, Delete, Storage, VerifiedUser, PhotoCamera } from '@mui/icons-material';
import useApi from 'hooks/useApi';
import { PERMISSIONS } from 'contexts/PermissionContext';
// import { useHasPermission } from 'contexts/PermissionContext';
import { PermissionWrapper } from 'components/PermissionComponents';
import JumioCountrySelect, { normalizeJumioCountry } from 'components/JumioCountrySelect';

interface Field {
  name: string;
  type: 'text' | 'number' | 'email' | 'date' | 'textarea' | 'dropdown' | 'image' | 'license_verification';
  required: boolean;
  unique?: boolean;
  dropdownSource?: 'teams' | 'countryCode';
  dropdownOptions?: string[];
  // `image` fields only. Which camera the mobile app opens — profile photos are
  // camera-only, so this is the ONLY lens the user gets.
  cameraFacing?: 'env' | 'user';
  // Document config for `license_verification` fields only — verification is
  // implied by the field type. Captured in the native app via the Jumio SDK.
  verification?: {
    provider?: string;
    documentType?: string;
    country?: string;
  } | null;
}

interface ResourceType {
  id: string;
  name: string;
  description: string;
  fields: Field[];
  createdAt: string;
  category?: 'user' | 'others';
  access?: string[];
  invite?: boolean;
  welcomeEmail?: boolean;
  permissions?: string[];
  editable?: boolean;
  deletable?: boolean;
  default?: boolean;
  allowRating?: boolean;
  restrictTransactionsToAssigned?: boolean;
}

interface ResourceTypeManagerProps {
  onRefresh: () => void;
  refreshTrigger: number;
  onResourceTypeSelect: (resourceType: ResourceType | null) => void;
  clientUID: string | null;
}

const ResourceTypeManager: React.FC<ResourceTypeManagerProps> = ({
  onRefresh,
  refreshTrigger,
  onResourceTypeSelect,
  clientUID
}) => {
  // Permission checks
  // const canEditResourceType = useHasPermission(PERMISSIONS.ADD_EDIT_RESOURCE_TYPE);
  // const canEditResource = useHasPermission(PERMISSIONS.ADD_EDIT_RESOURCE);

  const [resourceTypes, setResourceTypes] = useState<ResourceType[]>([]);
  const [open, setOpen] = useState(false);
  const [editingType, setEditingType] = useState<ResourceType | null>(null);
  const [loading, setLoading] = useState(true);
  const [nameError, setNameError] = useState<string>('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    fields: [] as Field[],
    category: 'others' as 'user' | 'others',
    access: [] as string[],
    invite: false,
    welcomeEmail: false,
    permissions: [] as string[],
    allowRating: false,
    restrictTransactionsToAssigned: false
  });
  const [fieldDialog, setFieldDialog] = useState(false);
  const [currentField, setCurrentField] = useState<Field>({
    name: '',
    type: 'text',
    required: false,
    unique: false,
    dropdownSource: undefined,
    dropdownOptions: undefined
  });

  // Default user fields that can be quickly added
  const defaultUserFields: Field[] = [
    { name: 'firstName', type: 'text', required: true, unique: false },
    { name: 'lastName', type: 'text', required: true, unique: false },
    { name: 'email', type: 'email', required: true, unique: true },
    { name: 'phoneNumber', type: 'text', required: true, unique: true },
    { name: 'team', type: 'dropdown', required: false, unique: false, dropdownSource: 'teams' },
    { name: 'countryCode', type: 'dropdown', required: true, unique: false, dropdownSource: 'countryCode' }
  ];

  // Available permissions for resource types
  const availablePermissions = [
    'Add/edit a process',
    'Workflow Edit',
    'Add/edit Notifications',
    'Add/edit hide-unhide Fields',
    'Add/edit AI Detection',
    'Add/edit Object Detection',
    'Add/edit transaction',
    'Export Transaction',
    'Bulk import Transactions',
    'Delete transactions',
    'Add/edit a Resource',
    'Add/edit a ResourceType',
    'Bulk upload Resources',
    'Dashboard Page Access',
    'Analytics page access',
    'Add/edit Integration Config',
    'Access All Private Chats',
    'Client page access',
    'Calls page access',
    'Transaction embed access',
    'AI Support page access',
    'Help page access'
  ];

  const { showToast, getResourceTypes, createResourceType, updateResourceType, deleteResourceType } = useApi();

  // Validation function to check for script tags and ensure proper string format
  const validateResourceTypeName = (name: string): { isValid: boolean; error?: string } => {
    const trimmedName = name.trim();
    
    if (!trimmedName) {
      return { isValid: false, error: 'Resource type name is required' };
    }
    
    // Check for script tags or HTML/JavaScript injection attempts
    const scriptTagRegex = /<script[^>]*>.*?<\/script>/gi;
    const htmlTagRegex = /<[^>]+>/g;
    const javascriptKeywords = /\b(javascript|eval|function|var|let|const|alert|confirm|prompt)\s*[\(\:]/i;
    
    if (scriptTagRegex.test(trimmedName)) {
      return { isValid: false, error: 'Script tags are not allowed in resource type name' };
    }
    
    if (htmlTagRegex.test(trimmedName)) {
      return { isValid: false, error: 'HTML tags are not allowed in resource type name' };
    }
    
    if (javascriptKeywords.test(trimmedName)) {
      return { isValid: false, error: 'JavaScript code is not allowed in resource type name' };
    }
    
    // Check for potentially dangerous characters
    const dangerousChars = /[<>"'&;\\]/;
    if (dangerousChars.test(trimmedName)) {
      return { isValid: false, error: 'Special characters like <, >, ", \', &, ; are not allowed in resource type name' };
    }
    
    // Ensure name contains only alphanumeric characters, spaces, hyphens, and underscores
    const validNameRegex = /^[a-zA-Z0-9\s\-_]+$/;
    if (!validNameRegex.test(trimmedName)) {
      return { isValid: false, error: 'Name can only contain letters, numbers, spaces, hyphens, and underscores' };
    }
    
    return { isValid: true };
  };

  useEffect(() => {
    loadResourceTypes();
  }, [refreshTrigger]);

  const loadResourceTypes = async () => {
    setLoading(true);
    try {
      const response = await getResourceTypes(clientUID || undefined, null);
      if (response.success) {
        setResourceTypes(response.data || []);
      } else {
        showToast('Failed to load resource types', 'error');
      }
    } catch (error) {
      showToast('Failed to load resource types', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async () => {
    // Validate resource type name
    const nameValidation = validateResourceTypeName(formData.name);
    if (!nameValidation.isValid) {
      showToast(nameValidation.error || 'Invalid resource type name', 'error');
      return;
    }

    if (formData.fields.length === 0) {
      showToast('At least one field is required', 'error');
      return;
    }

    // Jumio rejects anything but an ISO-3 country at scan time with an
    // unexplained 400 — catch it here instead. A valid ISO-2 code saved before
    // the picker existed is quietly upgraded to ISO-3.
    const badCountry = formData.fields.find(
      f => f.type === 'license_verification' && !normalizeJumioCountry(f.verification?.country).valid
    );
    if (badCountry) {
      showToast(`"${badCountry.name}" has an invalid document country — pick one from the list or clear it.`, 'error');
      return;
    }
    const fields = formData.fields.map(f =>
      f.type === 'license_verification' && f.verification
        ? { ...f, verification: { ...f.verification, country: normalizeJumioCountry(f.verification.country).country } }
        : f
    );

    try {
      let response;
      const payload = { 
        ...formData, 
        fields,
        clientUID: clientUID || undefined,
        editable: true,
        deletable: true
      };
      if (editingType) {
        response = await updateResourceType(editingType.id, payload);
      } else {
        response = await createResourceType(payload);
      }
      
      if (response.success) {
        showToast(
          editingType ? 'Resource type updated successfully' : 'Resource type created successfully',
          'success'
        );
        handleCloseDialog();
        onRefresh();
        loadResourceTypes();
      } else {
        showToast(response.message || 'Failed to save resource type', 'error');
      }
    } catch (error) {
      showToast('Failed to save resource type', 'error');
    }
  };

  const handleEdit = (resourceType: ResourceType) => {
    setEditingType(resourceType);
    
    let fieldsToUse = [...resourceType.fields];
    
    // For user category, ensure default user fields are included
    if (resourceType.category === 'user') {
      const existingFieldNames = fieldsToUse.map(f => f.name);
      const missingDefaultFields = defaultUserFields.filter(df => !existingFieldNames.includes(df.name));
      fieldsToUse = [...fieldsToUse, ...missingDefaultFields];
    }
    
    setFormData({
      name: resourceType.name,
      description: resourceType.description,
      fields: fieldsToUse,
      category: resourceType.category || 'others',
      access: [...(resourceType.access || [])],
      invite: resourceType.invite || false,
      welcomeEmail: resourceType.welcomeEmail || false,
      permissions: [...(resourceType.permissions || [])],
      allowRating: resourceType.allowRating || false,
      restrictTransactionsToAssigned: resourceType.restrictTransactionsToAssigned || false
    });
    
    setOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this resource type?')) {
      try {
        await deleteResourceType(id);
        showToast('Resource type deleted successfully', 'success');
        loadResourceTypes();
      } catch (error) {
        showToast('Failed to delete resource type', 'error');
      }
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingType(null);
    setNameError('');
    setFormData({ 
      name: '', 
      description: '', 
      fields: [],
      category: 'others',
      access: [],
      invite: false,
      welcomeEmail: false,
      permissions: [],
      allowRating: false,
    restrictTransactionsToAssigned: false
    });
  };

  const handleAddField = () => {
    if (!currentField.name.trim()) {
      showToast('Field name is required', 'error');
      return;
    }

    const existingField = formData.fields.find(f => f.name === currentField.name);
    if (existingField) {
      showToast('Field name already exists', 'error');
      return;
    }

    // For dropdown fields, validate dropdown source is selected
    if (currentField.type === 'dropdown' && !currentField.dropdownSource) {
      showToast('Please select a dropdown source', 'error');
      return;
    }

    // For dropdown fields, check if any sources are available
    if (currentField.type === 'dropdown') {
      const teamsAvailable = !formData.fields.some(f => f.type === 'dropdown' && f.dropdownSource === 'teams');
      const countryCodeAvailable = !formData.fields.some(f => f.type === 'dropdown' && f.dropdownSource === 'countryCode');
      
      if (!teamsAvailable && !countryCodeAvailable) {
        showToast('No dropdown sources are available. All sources are either in use or restricted by resource type configuration.', 'error');
        return;
      }
    }

    // Validate against resource type configuration conflicts
    if (currentField.type === 'dropdown') {
      // Check for duplicate dropdown sources
      const existingDropdownField = formData.fields.find(
        f => f.type === 'dropdown' && f.dropdownSource === currentField.dropdownSource
      );
      if (existingDropdownField) {
        showToast(
          `A dropdown field with source "${currentField.dropdownSource}" already exists (field: "${existingDropdownField.name}"). Each dropdown source can only be used once.`, 
          'error'
        );
        return;
      }
      
      if (currentField.dropdownSource === 'teams') {
        // Teams dropdown can always be used since we removed teamList restriction
      }
    }

    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, { ...currentField }]
    }));

    setCurrentField({ 
      name: '', 
      type: 'text', 
      required: false,
      unique: false,
      dropdownSource: undefined,
      dropdownOptions: undefined
    });
    setFieldDialog(false);
  };

  const handleRemoveField = (index: number) => {
    const fieldToRemove = formData.fields[index];
    
    // Prevent removal of default user fields when category is 'user'
    if (formData.category === 'user') {
      const isDefaultField = defaultUserFields.some(defaultField => defaultField.name === fieldToRemove?.name);
      if (isDefaultField) {
        showToast(`Cannot remove default user field "${fieldToRemove.name}". Default fields are required for user resource types.`, 'warning');
        return;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const addDefaultUserFields = () => {
    const existingFieldNames = formData.fields.map(field => field.name);
    const fieldsToAdd = defaultUserFields.filter(field => 
      !existingFieldNames.includes(field.name)
    );
    
    if (fieldsToAdd.length === 0) {
      showToast('All default user fields are already added', 'info');
      return;
    }

    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, ...fieldsToAdd]
    }));
    
    showToast(`Added ${fieldsToAdd.length} default user fields`, 'success');
  };

  const handleCategoryChange = (category: 'user' | 'others') => {
    setFormData(prev => ({
      ...prev,
      category,
      // Reset access-related fields when category changes
      access: [],
      invite: false,
      welcomeEmail: false,
      // Automatically add default user fields when category is user
      fields: category === 'user' ? [...defaultUserFields] : prev.fields
    }));
  };

  const handleAccessChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    setFormData(prev => ({
      ...prev,
      access: value
    }));
  };

  const handleInviteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      invite: event.target.checked
    }));
  };

  const handleWelcomeEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      welcomeEmail: event.target.checked
    }));
  };

  const handleAllowRatingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      allowRating: event.target.checked
    }));
  };

  const handleRestrictTransactionsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      restrictTransactionsToAssigned: event.target.checked
    }));
  };

  const handlePermissionChange = (permission: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: checked
        ? [...prev.permissions, permission]
        : prev.permissions.filter(p => p !== permission)
    }));
  };

  const getFieldTypeColor = (type: string) => {
    const colors: { [key: string]: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" } = {
      text: 'primary',
      number: 'info',
      email: 'secondary',
      date: 'warning',
      textarea: 'success',
      dropdown: 'error'
    };
    return colors[type] || 'default';
  };

  return (
    <Box>
      {/* Resource Types List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : resourceTypes.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'grey.50', borderRadius: 2, width: '100%' }}>
          <Storage sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Resource Types Available
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Create your first resource type to start organizing and managing your resources.
            Resource types define the structure and fields for your data.
          </Typography>
          <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_RESOURCE_TYPE} disableInsteadOfHide={true}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpen(true)}
              size="large"
            >
              Create Your First Resource Type
            </Button>
          </PermissionWrapper>
        </Box>
      ) : (
        <>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">Resource Types</Typography>
            <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_RESOURCE_TYPE} disableInsteadOfHide={true}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setOpen(true)}
              >
                Create Resource Type
              </Button>
            </PermissionWrapper>
          </Box>

          {/* Users Section */}
          {resourceTypes.filter(rt => rt.category === 'user').length > 0 && (
            <>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                User Resource Types
              </Typography>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {resourceTypes
                  .filter(rt => rt.category === 'user')
                  .map((resourceType) => (
                    <Grid item xs={12} md={6} lg={4} key={resourceType.id}>
                      <Card sx={{ border: '1px solid', borderColor: 'primary.light' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Storage sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="h6">{resourceType.name}</Typography>
                            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                              {resourceType.default && (
                                <Chip 
                                  label="Default" 
                                  size="small" 
                                  color="warning" 
                                  variant="filled"
                                  sx={{ fontWeight: 600 }}
                                />
                              )}
                              <Chip 
                                label="User" 
                                size="small" 
                                color="primary" 
                              />
                            </Box>
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {resourceType.description}
                          </Typography>

                          {/* Show access and invite info for user types */}
                          {resourceType.access && resourceType.access.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Access: 
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {resourceType.access.map((access, index) => (
                                  <Chip
                                    key={index}
                                    label={access}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {resourceType.invite && (
                            <Box sx={{ mb: 1 }}>
                              <Chip
                                label="Email Invite Enabled"
                                size="small"
                                color="info"
                                variant="outlined"
                              />
                            </Box>
                          )}

                          {resourceType.welcomeEmail && (
                            <Box sx={{ mb: 1 }}>
                              <Chip
                                label="Welcome Email Enabled"
                                size="small"
                                color="info"
                                variant="outlined"
                              />
                            </Box>
                          )}

                          {resourceType.allowRating && (
                            <Box sx={{ mb: 1 }}>
                              <Chip
                                label="Rating Enabled"
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            </Box>
                          )}

                          {resourceType.permissions?.includes('Access All Private Chats') && (
                            <Box sx={{ mb: 1 }}>
                              <Chip
                                label="Access All Private Chats"
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </Box>
                          )}

                          {resourceType.permissions?.includes('Analytics page access') && (
                            <Box sx={{ mb: 1 }}>
                              <Chip
                                label="Analytics page access"
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </Box>
                          )}

                          {resourceType.permissions?.includes('Client page access') && (
                            <Box sx={{ mb: 1 }}>
                              <Chip
                                label="Client page access"
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </Box>
                          )}

                          {resourceType.permissions?.includes('Calls page access') && (
                            <Box sx={{ mb: 1 }}>
                              <Chip
                                label="Calls page access"
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </Box>
                          )}

                          {resourceType.permissions?.includes('Transaction embed access') && (
                            <Box sx={{ mb: 1 }}>
                              <Chip
                                label="Transaction embed access"
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </Box>
                          )}

                          {resourceType.permissions?.includes('AI Support page access') && (
                            <Box sx={{ mb: 1 }}>
                              <Chip
                                label="AI Support page access"
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </Box>
                          )}

                          {resourceType.permissions?.includes('Help page access') && (
                            <Box sx={{ mb: 1 }}>
                              <Chip
                                label="Help page access"
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </Box>
                          )}

                          <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                            Fields ({resourceType.fields.length}):
                          </Typography>
                          
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                            {resourceType.fields.slice(0, 3).map((field, index) => (
                              <Chip
                                key={index}
                                label={field.type === 'dropdown' 
                                  ? `${field.name} (${field.type}: ${field.dropdownSource})`
                                  : `${field.name} (${field.type})`
                                }
                                size="small"
                                color={getFieldTypeColor(field.type)}
                                variant="outlined"
                              />
                            ))}
                            {resourceType.fields.length > 3 && (
                              <Chip
                                label={`+${resourceType.fields.length - 3} more`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>

                          {/* Show unique fields separately */}
                          {resourceType.fields.some(field => field.unique) && (
                            <>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Unique Fields ({resourceType.fields.filter(field => field.unique).length}):
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                                {resourceType.fields
                                  .filter(field => field.unique)
                                  .slice(0, 2)
                                  .map((field, index) => (
                                    <Chip
                                      key={index}
                                      label={field.name}
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                    />
                                  ))}
                                {resourceType.fields.filter(field => field.unique).length > 2 && (
                                  <Chip
                                    label={`+${resourceType.fields.filter(field => field.unique).length - 2} more`}
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                  />
                                )}
                              </Box>
                            </>
                          )}

                          {/* Show permissions if any */}
                          {resourceType.permissions && resourceType.permissions.filter(p => p !== 'Access All Private Chats').length > 0 && (
                            <>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Permissions ({resourceType.permissions.filter(p => p !== 'Access All Private Chats').length}):
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                                {resourceType.permissions.filter(p => p !== 'Access All Private Chats').slice(0, 2).map((permission, index) => (
                                  <Chip
                                    key={index}
                                    label={permission}
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                  />
                                ))}
                                {resourceType.permissions.filter(p => p !== 'Access All Private Chats').length > 2 && (
                                  <Chip
                                    label={`+${resourceType.permissions.filter(p => p !== 'Access All Private Chats').length - 2} more`}
                                    size="small"
                                    variant="outlined"
                                    color="info"
                                  />
                                )}
                              </Box>
                            </>
                          )}
                        </CardContent>
                        
                        <CardActions>
                          {resourceType.editable && !resourceType.default && (
                            <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_RESOURCE_TYPE} disableInsteadOfHide={true}>
                              <Button
                                size="small"
                                startIcon={<Edit />}
                                onClick={() => handleEdit(resourceType)}
                              >
                                Edit
                              </Button>
                            </PermissionWrapper>
                          )}
                          {resourceType.default && (
                            <Tooltip title="System default resource types cannot be edited">
                              <span>
                                <Button
                                  size="small"
                                  startIcon={<Edit />}
                                  disabled
                                  sx={{ color: 'text.disabled' }}
                                >
                                  Edit
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          {resourceType.deletable && !resourceType.default && (
                            <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_RESOURCE_TYPE} disableInsteadOfHide={true}>
                              <Button
                                size="small"
                                color="error"
                                startIcon={<Delete />}
                                onClick={() => handleDelete(resourceType.id)}
                              >
                                Delete
                              </Button>
                            </PermissionWrapper>
                          )}
                          {resourceType.default && (
                            <Tooltip title="System default resource types cannot be deleted">
                              <span>
                                <Button
                                  size="small"
                                  color="error"
                                  startIcon={<Delete />}
                                  disabled
                                  sx={{ color: 'text.disabled' }}
                                >
                                  Delete
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            </>
          )}

          {/* Others Section */}
          {resourceTypes.filter(rt => rt.category !== 'user').length > 0 && (
            <>
              <Typography variant="h6" sx={{ mb: 2, color: 'secondary.main' }}>
                Other Resource Types
              </Typography>
              <Grid container spacing={3}>
                {resourceTypes
                  .filter(rt => rt.category !== 'user')
                  .map((resourceType) => (
                    <Grid item xs={12} md={6} lg={4} key={resourceType.id}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Storage sx={{ mr: 1, color: 'secondary.main' }} />
                            <Typography variant="h6">{resourceType.name}</Typography>
                            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                              {resourceType.default && (
                                <Chip 
                                  label="Default" 
                                  size="small" 
                                  color="warning" 
                                  variant="filled"
                                  sx={{ fontWeight: 600 }}
                                />
                              )}
                              <Chip 
                                label="Other" 
                                size="small" 
                                color="secondary" 
                              />
                            </Box>
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {resourceType.description}
                          </Typography>

                          {/* Show rating enabled indicator */}
                          {resourceType.allowRating && (
                            <Box sx={{ mb: 1 }}>
                              <Chip
                                label="Rating Enabled"
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            </Box>
                          )}

                          {resourceType.permissions?.includes('Access All Private Chats') && (
                            <Box sx={{ mb: 1 }}>
                              <Chip
                                label="Access All Private Chats"
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </Box>
                          )}

                          {resourceType.permissions?.includes('Analytics page access') && (
                            <Box sx={{ mb: 1 }}>
                              <Chip
                                label="Analytics page access"
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </Box>
                          )}

                          {resourceType.permissions?.includes('Client page access') && (
                            <Box sx={{ mb: 1 }}>
                              <Chip
                                label="Client page access"
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </Box>
                          )}

                          {resourceType.permissions?.includes('Calls page access') && (
                            <Box sx={{ mb: 1 }}>
                              <Chip
                                label="Calls page access"
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </Box>
                          )}

                          {resourceType.permissions?.includes('Transaction embed access') && (
                            <Box sx={{ mb: 1 }}>
                              <Chip
                                label="Transaction embed access"
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </Box>
                          )}

                          {resourceType.permissions?.includes('AI Support page access') && (
                            <Box sx={{ mb: 1 }}>
                              <Chip
                                label="AI Support page access"
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </Box>
                          )}

                          {resourceType.permissions?.includes('Help page access') && (
                            <Box sx={{ mb: 1 }}>
                              <Chip
                                label="Help page access"
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </Box>
                          )}

                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Fields ({resourceType.fields.length}):
                          </Typography>
                          
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                            {resourceType.fields.slice(0, 3).map((field, index) => (
                              <Chip
                                key={index}
                                label={field.type === 'dropdown' 
                                  ? `${field.name} (${field.type}: ${field.dropdownSource})`
                                  : `${field.name} (${field.type})`
                                }
                                size="small"
                                color={getFieldTypeColor(field.type)}
                                variant="outlined"
                              />
                            ))}
                            {resourceType.fields.length > 3 && (
                              <Chip
                                label={`+${resourceType.fields.length - 3} more`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>

                          {/* Show unique fields separately */}
                          {resourceType.fields.some(field => field.unique) && (
                            <>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Unique Fields ({resourceType.fields.filter(field => field.unique).length}):
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                                {resourceType.fields
                                  .filter(field => field.unique)
                                  .slice(0, 2)
                                  .map((field, index) => (
                                    <Chip
                                      key={index}
                                      label={field.name}
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                    />
                                  ))}
                                {resourceType.fields.filter(field => field.unique).length > 2 && (
                                  <Chip
                                    label={`+${resourceType.fields.filter(field => field.unique).length - 2} more`}
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                  />
                                )}
                              </Box>
                            </>
                          )}
                        </CardContent>
                        
                        <CardActions>
                          {resourceType.editable && !resourceType.default && (
                            <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_RESOURCE_TYPE} disableInsteadOfHide={true}>
                              <Button
                                size="small"
                                startIcon={<Edit />}
                                onClick={() => handleEdit(resourceType)}
                              >
                                Edit
                              </Button>
                            </PermissionWrapper>
                          )}
                          {resourceType.default && (
                            <Tooltip title="System default resource types cannot be edited">
                              <span>
                                <Button
                                  size="small"
                                  startIcon={<Edit />}
                                  disabled
                                  sx={{ color: 'text.disabled' }}
                                >
                                  Edit
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          {resourceType.deletable && !resourceType.default && (
                            <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_RESOURCE_TYPE} disableInsteadOfHide={true}>
                              <Button
                                size="small"
                                color="error"
                                startIcon={<Delete />}
                                onClick={() => handleDelete(resourceType.id)}
                              >
                                Delete
                              </Button>
                            </PermissionWrapper>
                          )}
                          {resourceType.default && (
                            <Tooltip title="System default resource types cannot be deleted">
                              <span>
                                <Button
                                  size="small"
                                  color="error"
                                  startIcon={<Delete />}
                                  disabled
                                  sx={{ color: 'text.disabled' }}
                                >
                                  Delete
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            </>
          )}

          {/* Show message if no resource types in either category */}
          {resourceTypes.filter(rt => rt.category === 'user').length === 0 && 
           resourceTypes.filter(rt => rt.category !== 'user').length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Storage sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Resource Types Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create your first resource type to get started.
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog 
        open={open} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '85vh'
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h6">
            {editingType ? 'Edit Resource Type' : 'Create Resource Type'}
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => {
                const newName = e.target.value;
                setFormData(prev => ({ ...prev, name: newName }));
                
                // Validate name on change
                if (newName.trim()) {
                  const validation = validateResourceTypeName(newName);
                  setNameError(validation.isValid ? '' : validation.error || '');
                } else {
                  setNameError('');
                }
              }}
              error={!!nameError}
              helperText={nameError || 'Enter a name for your resource type (letters, numbers, spaces, hyphens, underscores only)'}
              sx={{ mb: 3 }}
            />
            
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              sx={{ mb: 3 }}
            />

            {/* Category Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => handleCategoryChange(e.target.value as 'user' | 'others')}
              >
                <MenuItem value="others">Others</MenuItem>
                <MenuItem value="user">User</MenuItem>
              </Select>
            </FormControl>

            {/* Access Selection - Only show for user category */}
            {formData.category === 'user' && (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Access</InputLabel>
                <Select
                  multiple
                  value={formData.access}
                  label="Access"
                  onChange={handleAccessChange}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  <MenuItem value="Admin Panel">Admin Panel</MenuItem>
                  <MenuItem value="App">App</MenuItem>
                </Select>
              </FormControl>
            )}

            {/* Invite Toggle - Only show for user category */}
            {formData.category === 'user' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.invite}
                    onChange={handleInviteChange}
                  />
                }
                label="Invite via email with reset password link"
                sx={{ mb: 3 }}
              />
            )}

            {/* Welcome Email Toggle - Only show for user category */}
            {formData.category === 'user' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.welcomeEmail}
                    onChange={handleWelcomeEmailChange}
                  />
                }
                label="Send welcome email to new users"
                sx={{ mb: 3 }}
              />
            )}

            {/* Allow Rating Toggle - Only show for user category */}
            {formData.category === 'user' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.allowRating}
                    onChange={handleAllowRatingChange}
                  />
                }
                label="Allow resource rating in transactions"
                sx={{ mb: 3 }}
              />
            )}

            {/* Transaction visibility - Only show for user category */}
            {formData.category === 'user' && (
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.restrictTransactionsToAssigned}
                      onChange={handleRestrictTransactionsChange}
                    />
                  }
                  label="Only show transactions assigned to this role"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6, mt: -0.5 }}>
                  When on, these users see a process&apos;s transactions only if the transaction is assigned to
                  them or to this role, or they have already worked on it. When off, they see every transaction
                  in their processes.
                </Typography>
                <Typography variant="caption" color="warning.main" sx={{ display: 'block', ml: 6, mt: 0.75 }}>
                  Transactions are assigned through a RESOURCE form field marked as assignee. A process with no
                  such field cannot assign anything, so it keeps normal visibility rather than showing nothing.
                </Typography>
              </Box>
            )}

            {/* Permissions - Only show for user category */}
            {formData.category === 'user' && (
              <Box 
                sx={{ 
                  mb: 3, 
                  p: 3, 
                  backgroundColor: 'grey.50', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                  Permissions
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Configure what actions users with this resource type can perform in the system. 
                  Select the appropriate permissions based on the role and responsibilities of these users.
                </Typography>
                <Grid container spacing={2}>
                  {availablePermissions.map((permission) => (
                    <Grid item xs={12} sm={6} key={permission}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.permissions.includes(permission)}
                            onChange={(e) => handlePermissionChange(permission, e.target.checked)}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {permission}
                          </Typography>
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
                {formData.permissions.length > 0 && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'grey.300' }}>
                    <Typography variant="caption" color="text.secondary">
                      Selected permissions: {formData.permissions.length} of {availablePermissions.length}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Fields</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={addDefaultUserFields}
                  size="small"
                  color="secondary"
                >
                  Add User Fields
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => setFieldDialog(true)}
                >
                  Add Field
                </Button>
              </Box>
            </Box>

            {formData.fields.length === 0 && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Quick Start:</strong> Click "Add User Fields" to automatically add common user fields 
                  (firstName, lastName, email, phoneNumber, team, countryCode), or use "Add Field" to create custom fields.
                </Typography>
              </Box>
            )}

            <List>
              {formData.fields.map((field, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                          <Typography variant="subtitle1">{field.name}</Typography>
                          <Chip
                            label={field.type === 'dropdown'
                              ? `${field.type}: ${field.dropdownSource}`
                              : field.type === 'license_verification'
                                ? 'licence'
                                : field.type
                            }
                            size="small"
                            color={getFieldTypeColor(field.type)}
                          />
                          {field.required && (
                            <Chip label="Required" size="small" color="error" />
                          )}
                          {field.unique && (
                            <Chip label="Unique" size="small" color="warning" />
                          )}
                          {formData.category === 'user' &&
                           defaultUserFields.some(defaultField => defaultField.name === field.name) && (
                            <Chip label="Default" size="small" color="info" />
                          )}
                          {field.type === 'image' && (
                            <Tooltip title="Camera only — no gallery upload in the mobile app">
                              <Chip
                                icon={<PhotoCamera sx={{ fontSize: 14 }} />}
                                label={field.cameraFacing === 'user' ? 'Front camera' : 'Back camera'}
                                size="small"
                                variant="outlined"
                              />
                            </Tooltip>
                          )}
                          {field.type === 'license_verification' && (
                            <Tooltip
                              title={'Licence verification — scanned in the mobile app with the Jumio SDK'
                                + ` · ${field.verification?.documentType || 'DRIVING_LICENSE'}`
                                + (field.verification?.country ? ` · ${field.verification.country}` : '')}
                            >
                              <Chip
                                icon={<VerifiedUser sx={{ fontSize: 14 }} />}
                                label="Jumio SDK"
                                size="small"
                                color="success"
                              />
                            </Tooltip>
                          )}
                          {/* Fields can't be edited in place, so a country saved
                              before the picker existed is fixed right here —
                              otherwise every scan fails and the type can't be saved. */}
                          {field.type === 'license_verification' &&
                           !normalizeJumioCountry(field.verification?.country).valid && (
                            <JumioCountrySelect
                              sx={{ minWidth: 280, mt: 1, flexBasis: '100%' }}
                              value={field.verification?.country ?? ''}
                              onChange={(iso3) => setFormData(prev => ({
                                ...prev,
                                fields: prev.fields.map((f, i) =>
                                  i === index ? { ...f, verification: { ...(f.verification || {}), country: iso3 } } : f
                                )
                              }))}
                            />
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveField(index)}
                        color="error"
                        disabled={
                          formData.category === 'user' && 
                          defaultUserFields.some(defaultField => defaultField.name === field.name)
                        }
                        title={
                          formData.category === 'user' && 
                          defaultUserFields.some(defaultField => defaultField.name === field.name)
                            ? 'Cannot remove default user field'
                            : 'Remove field'
                        }
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < formData.fields.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleCreateOrUpdate} variant="contained">
            {editingType ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Field Dialog */}
      <Dialog 
        open={fieldDialog} 
        onClose={() => setFieldDialog(false)}
        maxWidth="xs" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h6">
            Add Field
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Field Name"
              value={currentField.name}
              onChange={(e) => setCurrentField(prev => ({ ...prev, name: e.target.value }))}
            />
            
            <FormControl fullWidth>
              <InputLabel>Field Type</InputLabel>
              <Select
                value={currentField.type}
                label="Field Type"
                onChange={(e) => setCurrentField(prev => ({
                  ...prev,
                  type: e.target.value as Field['type'],
                  dropdownSource: undefined,
                  dropdownOptions: undefined,
                  // Set unique to false for dropdown fields
                  unique: e.target.value === 'dropdown' ? false : prev.unique,
                  // An image field always has a camera — seed it so the value is
                  // there even if the admin never touches the selector.
                  ...(e.target.value === 'image'
                    ? { cameraFacing: prev.cameraFacing ?? 'env' }
                    : {}),
                  // A licence field is always verified — seed the config so it is
                  // valid even if the admin never opens the section below.
                  ...(e.target.value === 'license_verification'
                    ? {
                        verification: {
                          ...(prev.verification || {}),
                          provider: 'jumio',
                          documentType: prev.verification?.documentType ?? 'DRIVING_LICENSE',
                        },
                      }
                    : {}),
                }))}
              >
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="textarea">Text Area</MenuItem>
                <MenuItem value="number">Number</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="date">Date</MenuItem>
                <MenuItem value="dropdown">Dropdown</MenuItem>
                <MenuItem value="image">Image</MenuItem>
                <MenuItem value="license_verification">License Verification (Jumio)</MenuItem>
              </Select>
            </FormControl>

            {/* Dropdown Source - Only show for dropdown fields */}
            {currentField.type === 'dropdown' && (
              <FormControl fullWidth>
                <InputLabel>Dropdown Source</InputLabel>
                <Select
                  value={currentField.dropdownSource || ''}
                  label="Dropdown Source"
                  onChange={(e) => setCurrentField(prev => ({ 
                    ...prev, 
                    dropdownSource: e.target.value as 'teams' | 'countryCode'
                  }))}
                  disabled={
                    // Disable if no options are available
                    formData.fields.some(f => f.type === 'dropdown' && f.dropdownSource === 'teams') &&
                    formData.fields.some(f => f.type === 'dropdown' && f.dropdownSource === 'countryCode')
                  }
                >
                  {/* Only show teams option if not already used */}
                  {!formData.fields.some(f => f.type === 'dropdown' && f.dropdownSource === 'teams') && (
                    <MenuItem value="teams">Teams</MenuItem>
                  )}
                  {/* Only show country code if not already used */}
                  {!formData.fields.some(f => f.type === 'dropdown' && f.dropdownSource === 'countryCode') && (
                    <MenuItem value="countryCode">Country Code</MenuItem>
                  )}
                  
                  {/* Show message if no options available */}
                  {formData.fields.some(f => f.type === 'dropdown' && f.dropdownSource === 'teams') &&
                   formData.fields.some(f => f.type === 'dropdown' && f.dropdownSource === 'countryCode') && (
                    <MenuItem disabled>No dropdown sources available</MenuItem>
                  )}
                </Select>
                
                {/* Show helper text when dropdown is disabled */}
                {(formData.fields.some(f => f.type === 'dropdown' && f.dropdownSource === 'teams') &&
                  formData.fields.some(f => f.type === 'dropdown' && f.dropdownSource === 'countryCode')) && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    All dropdown sources are already in use.
                  </Typography>
                )}
              </FormControl>
            )}

            {/* Show error message when dropdown is selected but no sources are available */}
            {/* {currentField.type === 'dropdown' && (
              (() => {
                const teamsAvailable = !formData.fields.some(f => f.type === 'dropdown' && f.dropdownSource === 'teams');
                const countryCodeAvailable = !formData.fields.some(f => f.type === 'dropdown' && f.dropdownSource === 'countryCode');
                
                if (!teamsAvailable && !countryCodeAvailable) {
                  return (
                    <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1, mb: 2 }}>
                      <Typography variant="body2" color="error.dark">
                        ❌ <strong>No dropdown sources available!</strong><br />
                        All dropdown sources are already in use. Please select a different field type or remove existing dropdown fields to free up sources.
                      </Typography>
                    </Box>
                  );
                }
                return null;
              })()
            )} */}

            {/* Show info about dropdown source */}
            {currentField.type === 'dropdown' && currentField.dropdownSource && (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {currentField.dropdownSource === 'teams' && 
                    'This dropdown will dynamically load teams for the selected client.'}
                  {currentField.dropdownSource === 'countryCode' && 
                    'This dropdown will show a predefined list of country codes (e.g., +1, +91, +44).'}
                </Typography>
              </Box>
            )}

            {/* Show warning about conflicts */}
            {currentField.type === 'dropdown' && (
              <Box>
                {/* Show which dropdown sources are already in use */}
                {(() => {
                  const usedSources = formData.fields
                    .filter(f => f.type === 'dropdown' && f.dropdownSource)
                    .map(f => ({ source: f.dropdownSource!, fieldName: f.name }));
                  
                  if (usedSources.length > 0) {
                    return (
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          📋 Dropdown sources already in use:
                        </Typography>
                        {usedSources.map(({ source, fieldName }, index) => (
                          <Typography key={index} variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                            • {source} (field: "{fieldName}")
                          </Typography>
                        ))}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                          Each dropdown source can only be used once to prevent conflicts.
                        </Typography>
                      </Box>
                    );
                  }
                  return null;
                })()}
              </Box>
            )}

            <FormControl fullWidth>
              <InputLabel>Required</InputLabel>
              <Select
                value={currentField.required ? 'yes' : 'no'}
                label="Required"
                onChange={(e) => setCurrentField(prev => ({ 
                  ...prev, 
                  required: e.target.value === 'yes'
                }))}
              >
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Unique</InputLabel>
              <Select
                value={currentField.unique ? 'yes' : 'no'}
                label="Unique"
                disabled={currentField.type === 'dropdown'}
                onChange={(e) => setCurrentField(prev => ({ 
                  ...prev, 
                  unique: e.target.value === 'yes'
                }))}
              >
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </Select>
              {currentField.type === 'dropdown' && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Dropdown fields cannot be marked as unique since they represent selections from predefined lists.
                </Typography>
              )}
            </FormControl>

            {/* Camera for image fields. The mobile app captures these with the
                camera only — there is no gallery option — so this is not a
                preference, it is the lens the user gets, and the in-app flip
                control is hidden. */}
            {currentField.type === 'image' && (
              <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <PhotoCamera fontSize="small" color="primary" />
                  <Typography variant="subtitle2">Camera</Typography>
                </Stack>

                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <InputLabel>Camera</InputLabel>
                  <Select
                    value={currentField.cameraFacing ?? 'env'}
                    label="Camera"
                    onChange={(e) => setCurrentField(prev => ({
                      ...prev,
                      cameraFacing: e.target.value as 'env' | 'user',
                    }))}
                  >
                    <MenuItem value="env">Back camera</MenuItem>
                    <MenuItem value="user">Front camera (selfie)</MenuItem>
                  </Select>
                </FormControl>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  In the mobile app this photo can only be taken with the camera —
                  choosing an existing picture from the gallery is not offered — and
                  the user cannot switch to the other camera.
                </Typography>
              </Box>
            )}

            {/* Licence verification config. Unlike the image-field switch below,
                verification is not optional here — it is the whole point of the
                field type — so there is no enable toggle, only the document
                config the mobile SDK needs. */}
            {currentField.type === 'license_verification' && (
              <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <VerifiedUser fontSize="small" color="success" />
                  <Typography variant="subtitle2">Licence verification (Jumio)</Typography>
                </Stack>

                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <InputLabel>Document Type</InputLabel>
                  <Select
                    value={currentField.verification?.documentType ?? 'DRIVING_LICENSE'}
                    label="Document Type"
                    onChange={(e) => setCurrentField(prev => ({
                      ...prev,
                      verification: {
                        ...(prev.verification || {}),
                        provider: 'jumio',
                        documentType: e.target.value as string,
                      },
                    }))}
                  >
                    <MenuItem value="DRIVING_LICENSE">Driving License</MenuItem>
                    <MenuItem value="ID">ID Card</MenuItem>
                    <MenuItem value="PASSPORT">Passport</MenuItem>
                  </Select>
                </FormControl>

                <JumioCountrySelect
                  sx={{ mt: 1.5 }}
                  value={currentField.verification?.country ?? ''}
                  onChange={(iso3) => setCurrentField(prev => ({
                    ...prev,
                    verification: {
                      ...(prev.verification || {}),
                      provider: 'jumio',
                      country: iso3,
                    },
                  }))}
                  helperText="Optional — leave blank to accept any country. Required for the US DMV / AAMVA data check → USA."
                />

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  The driver scans both sides of the document in the mobile app using the
                  Jumio SDK. The captured images and the extracted details are stored on
                  this field automatically — nothing is uploaded from the dashboard.
                </Typography>
              </Box>
            )}

          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setFieldDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleAddField} 
            variant="contained"
            disabled={
              // Disable if dropdown is selected but no source is chosen or no sources available
              currentField.type === 'dropdown' && (
                !currentField.dropdownSource || 
                (
                  // Check if no dropdown sources are available
                  formData.fields.some(f => f.type === 'dropdown' && f.dropdownSource === 'teams') &&
                  formData.fields.some(f => f.type === 'dropdown' && f.dropdownSource === 'countryCode')
                )
              )
            }
          >
            Add Field
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResourceTypeManager;
